import {Button, Paper} from '@material-ui/core';
import {Loaded} from 'Core/models/Async';
import {identity} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {ap as apOption, fold as foldOption, fromNullable, some} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Lens} from 'monocle-ts';
import {EncryptedTaxIdentificationNumber} from 'Agent/Policies/models/TaxIdentificationNumber';
import qs from 'query-string';
import React, {useEffect} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router';
import {ReducerState} from 'rootReducer';
import Alert from 'Shared/components/Alert';
import EmailInput from 'Shared/components/EmailInput';
import Loading from 'Shared/components/Loading';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import RequiredTextInput from 'Shared/components/RequiredTextInput';
import {Scope} from 'Shared/constants/Scope';
import {NoOp} from 'Shared/utilities/IOUtilities';
import {parseBoolean} from 'Shared/utilities/parsers';
import {toThunk} from 'Shared/utilities/toThunk';
import AgencyInput from 'Users/components/AgentAdministration/AgencyInput';
import AgencySupportVisibilityList from 'Users/components/AgentAdministration/AgencySupportVisibilityList';
import AgentNumberInput from 'Users/components/AgentAdministration/AgentNumberInput';
import {fetchAgentNumbersByTaxIdentifier} from 'Users/ducks/agentNumber';
import * as IndependentAgentActions from 'Users/ducks/independentAgent';
import {ValidAgencyAssociation} from 'Users/models/AgencyAssociation';
import {
  IndependentAgentRegistration,
  IndependentAgentRegistrationWithError,
  InvalidIndependentAgentRegistration,
  matchIndependentAgentRegistration,
  UnvalidatedIndependentAgentRegistration,
  ValidIndependentAgentRegistration,
} from 'Users/models/IndependentAgent';
import {selectCurrentIdentifier, selectCurrentTaxId, selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './IndependentAgentEntry.sass';
import {ValidationError, changeAgentErrors} from 'Users/ducks/independentAgent';

type PropsFromRedux = ReturnType<typeof mapStateToProps> & typeof IndependentAgentActions;

type IndependentAgentEntryProps = RouteComponentProps<{userId: string}> & PropsFromRedux;
const independentAgentLens = Lens.fromProp<IndependentAgentRegistration>();

type IndependentAgentFormProps = {
  onChangeAgent: (agentRegistration: IndependentAgentRegistration) => void;
  independentAgent: IndependentAgentRegistration;
  currentTaxId: string;
  errors: ValidationError[];
  changeAgentErrors: typeof changeAgentErrors
} & PropsFromRedux &
  typeof IndependentAgentActions &
  RouteComponentProps;

export const validateName = (fieldName: string, value: string) => {
  const regex = /^([a-zA-Z0-9, \-'.])+$/;
  if (!regex.test(value)) {
    return `${fieldName} is Invalid`;
  }
};

const IndependentAgentForm = (props: IndependentAgentFormProps) => {
  const dispatch = useDispatch();
  const {independentAgent, errors} = props;
  const {selectedAgency} = useSelector((state: ReducerState) => state.user.profile);
  const encryptedIdentifier = useSelector((state: ReducerState) => selectCurrentIdentifier(state));
  const isSupport = parseBoolean(qs.parse(props.location.search).isSupport as string);

  useEffect(() => {
    if (encryptedIdentifier) {
      dispatch(fetchAgentNumbersByTaxIdentifier(encryptedIdentifier, props.currentTaxId, encryptedIdentifier));
    }
    return () => {
      dispatch(props.changeAgentErrors([]));
    };
  }, []);

  return (
    <Paper className="agent-entry">
      {independentAgent instanceof IndependentAgentRegistrationWithError && (
        <Alert message={independentAgent.errorMessage} />
      )}
      <h2 className="agent-entry__title">Add {isSupport ? 'Agent Support' : 'Agent'}</h2>
      <p className="agent-entry__description">Please enter the agent's information below to grant them access.</p>
      <form className="agent-entry__form">
        <RequiredTextInput
          className="independent-agent-entry__field"
          label="First Name"
          onChange={firstName =>
            props.onChangeAgent(independentAgentLens('firstName').set(firstName)(props.independentAgent))
          }
          value={props.independentAgent.firstName}
          onValidate={validateName}
          additionalErrors={errors}
          changeError={props.changeAgentErrors}
        />
        <RequiredTextInput
          className="independent-agent-entry__field"
          label="Last Name"
          onChange={lastName =>
            props.onChangeAgent(independentAgentLens('lastName').set(lastName)(props.independentAgent))
          }
          value={props.independentAgent.lastName}
          onValidate={validateName}
          additionalErrors={errors}
          changeError={props.changeAgentErrors}
        />
        <EmailInput
          className="independent-agent-entry__field"
          variant="outlined"
          email={props.independentAgent.email}
          onChange={email => {
            props.onChangeAgent(independentAgentLens('email').set(email)(props.independentAgent));
          }}
        />
        {props.scopes.includes(Scope.AddIndependentAgentPreregistrationToAgency) ||
        props.scopes.some(scope => scope === Scope.AddPreregistrationUsers + '|*') ? (
          <AgencyInput
            isSupport={isSupport}
            scopes={props.scopes}
            agencyAssociations={props.independentAgent.agencyAssociations}
            changeAssociations={associations =>
              props.onChangeAgent(independentAgentLens('agencyAssociations').set(associations)(props.independentAgent))
            }
          />
        ) : (
          <>
            <AgentNumberInput
              className="independent-agent-entry__field"
              agentNumbers={
                props.independentAgent.agencyAssociations.length > 0
                  ? props.independentAgent.agencyAssociations[0].agentNumbers
                  : []
              }
              uniqueKey={props.currentTaxId}
              onChangeAgentNumber={numbers => {
                props.onChangeAgent(
                  independentAgentLens('agencyAssociations').set([
                    ValidAgencyAssociation.create(
                      'association1',
                      false,
                      new EncryptedTaxIdentificationNumber(props.encryptedIdentifier),
                      numbers,
                      []
                    ),
                  ])(props.independentAgent)
                );
              }}
            />
            {isSupport && (
              <AgencySupportVisibilityList
                visibilityPermissions={
                  props.independentAgent.agencyAssociations.length > 0
                    ? props.independentAgent.agencyAssociations[0].visibilityPermissions
                    : []
                }
                onChange={permissions => {
                  props.onChangeAgent(
                    independentAgentLens('agencyAssociations').set([
                      ValidAgencyAssociation.create(
                        'association1',
                        false,
                        new EncryptedTaxIdentificationNumber(props.encryptedIdentifier),
                        props.independentAgent.agencyAssociations.length > 0
                          ? props.independentAgent.agencyAssociations[0].agentNumbers
                          : [],
                        permissions
                      ),
                    ])(props.independentAgent)
                  );
                }}
              />
            )}
          </>
        )}
        <div className="agent-entry-form__buttons">
          <div className="agent-entry-form__return-button">
            <Button
              id="independent-agent-entry-cancel-button"
              variant="contained"
              onClick={() => {
                props.history.goBack();
              }}
            >
              Cancel
            </Button>
          </div>
          <Button
            id="independent-agent-entry-save-button"
            className="agent-entry-form__add-button"
            color="primary"
            role="submit"
            variant="contained"
            onClick={() =>
              props.preregisterAgent(
                props.independentAgent as ValidIndependentAgentRegistration,
                O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined
              )
            }
            disabled={!(props.independentAgent instanceof ValidIndependentAgentRegistration) || errors.length > 0}
          >
            Save
          </Button>
        </div>
      </form>
    </Paper>
  );
};

export const IndependentAgentEntry = (props: IndependentAgentEntryProps) => {
  useEffect(() => {
    return () => {
      props.clear();
    };
  }, []);
  const existingIndependentAgentId = fromNullable(props.match.params.userId);
  const getExistingAgent = toThunk(props.fetchIndependentAgent);
  const getExistingAgentIfEditing = pipe(
    some(getExistingAgent),
    apOption(existingIndependentAgentId),
    foldOption(() => NoOp, identity)
  );

  useEffect(getExistingAgentIfEditing, []);
  const renderIndependentAgentForm = (
    independentAgent:
      | UnvalidatedIndependentAgentRegistration
      | InvalidIndependentAgentRegistration
      | ValidIndependentAgentRegistration
      | IndependentAgentRegistrationWithError
  ) => {
    return (
      <IndependentAgentForm
        {...props}
        currentTaxId={props.currentTaxId}
        encryptedIdentifier={props.encryptedIdentifier}
        independentAgent={independentAgent}
        errors={props.errors}
        changeAgentErrors={props.changeAgentErrors}
        onChangeAgent={agent =>
          props.changeIndpendentAgent(
            new Loaded(
              ValidIndependentAgentRegistration.create(
                agent.firstName,
                agent.lastName,
                agent.email,
                agent.agencyAssociations,
                agent.agentNumbers
              )
            )
          )
        }
      />
    );
  };

  return renderAsyncView(props.asyncIA, independentAgent =>
    matchIndependentAgentRegistration(independentAgent, {
      InvalidIndependentAgentRegistration: renderIndependentAgentForm,
      ValidIndependentAgentRegistration: renderIndependentAgentForm,
      IndependentAgentRegistrationWithError: renderIndependentAgentForm,
      UnvalidatedIndependentAgentRegistration: renderIndependentAgentForm,
      SavingIndependentAgentRegistration: () => <Loading />,
    })
  );
};

const mapStateToProps = (state: ReducerState) => ({
  asyncIA: state.user.independentAgent.independentAgent,
  currentTaxId: selectCurrentTaxId(state),
  encryptedIdentifier: selectCurrentIdentifier(state),
  scopes: selectUserScopes(state),
  errors: state.user.independentAgent.errors,
});

export default connect(mapStateToProps, {
  ...IndependentAgentActions,
})(IndependentAgentEntry);
